import * as LiveKit from "livekit-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { getAudioManager } from "@/services/AudioManager";
import { decodeRoomMessage, isSongMessage } from "@/types/room-message";
import type { Station } from "@/lib/api-client";

interface UseStationConnectionOptions {
  station: Station | null;
  autoConnect?: boolean;
}

interface NowPlaying {
  name: string;
  artist: string;
  album: string;
  timestamp: number;
}

interface UseStationConnectionReturn {
  isConnected: boolean;
  isConnecting: boolean;
  error: Error | null;
  isAutoplayBlocked: boolean;
  connect: () => void;
  disconnect: () => void;
  unmute: () => Promise<void>;
  nowPlaying: NowPlaying | null;
}

export function useStationConnection({
  station,
  autoConnect = true,
}: UseStationConnectionOptions): UseStationConnectionReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [nowPlaying, setNowPlaying] = useState<NowPlaying | null>(null);
  const [isAutoplayBlocked, setIsAutoplayBlocked] = useState(false);

  const roomRef = useRef<LiveKit.Room | null>(null);
  const hasAttemptedAutoplay = useRef(false);
  const audioManager = getAudioManager();

  // Get viewer token mutation
  const { mutateAsync: joinStationAsViewer } = useMutation({
    mutationFn: apiClient.stations.joinAsReceiver,
  });

  // Handle audio track attachment and autoplay
  const handleTrackSubscribed = useCallback(
    async (
      track: LiveKit.RemoteTrack,
      _publication: LiveKit.RemoteTrackPublication,
      _participant: LiveKit.RemoteParticipant
    ) => {
      if (track.kind !== "audio") return;

      const audioTrack = track as LiveKit.RemoteAudioTrack;
      const audioElement = audioTrack.attach();
      audioElement.style.display = "none";
      document.body.appendChild(audioElement);

      // Add to audio manager
      audioManager.addAudioElement(
        track.sid || `track-${Date.now()}`,
        audioElement
      );

      // Attempt autoplay only once, when the first audio track is attached
      if (!hasAttemptedAutoplay.current) {
        hasAttemptedAutoplay.current = true;

        // Small delay to ensure element is ready
        setTimeout(async () => {
          try {
            await audioElement.play();
            // Success! Audio is playing
            setIsAutoplayBlocked(false);
          } catch (error) {
            if (error instanceof Error && error.name === "NotAllowedError") {
              // Autoplay was blocked - don't try to play at all (browsers block even muted audio)
              // Audio will remain paused until user clicks, then we'll unmute and play
              console.log("[StationConnection] Autoplay blocked, waiting for user interaction");
              audioElement.muted = true;
              setIsAutoplayBlocked(true);
              setError(error);
            }
          }
        }, 100);
      }
    },
    [audioManager]
  );

  // Connect to station
  const connect = useCallback(async () => {
    if (!station || !station.isActive || roomRef.current || isConnecting) {
      return;
    }

    setIsConnecting(true);
    setError(null);
    hasAttemptedAutoplay.current = false;

    try {
      // Get viewer token
      const { token } = await joinStationAsViewer({
        stationId: station.id,
        receiverName: `viewer-${Date.now()}`,
      });

      // Create LiveKit room
      const room = new LiveKit.Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: LiveKit.VideoPresets.h1080,
        },
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Set up event handlers
      room.on(LiveKit.RoomEvent.Connected, () => {
        setIsConnected(true);
        setIsConnecting(false);
      });

      room.on(LiveKit.RoomEvent.Disconnected, () => {
        setIsConnected(false);
        setNowPlaying(null);
        // Clean up audio elements
        audioManager.cleanup();
      });

      room.on(LiveKit.RoomEvent.TrackSubscribed, handleTrackSubscribed);

      room.on(
        LiveKit.RoomEvent.TrackUnsubscribed,
        (track: LiveKit.RemoteTrack) => {
          if (track.kind === "audio" && track.sid) {
            audioManager.removeAudioElement(track.sid);
            track.detach();
          }
        }
      );

      // Listen for data channel messages (Now Playing updates)
      room.on(
        LiveKit.RoomEvent.DataReceived,
        (payload: Uint8Array, participant?: LiveKit.RemoteParticipant) => {
          // Only process messages from the host (publisher)
          if (participant && participant.permissions?.canPublish) {
            const message = decodeRoomMessage(payload);
            
            if (message && isSongMessage(message)) {
              // Update Now Playing state
              setNowPlaying({
                name: message.data.name,
                artist: message.data.artist,
                album: message.data.album,
                timestamp: Date.now(),
              });
            }
          }
        }
      );

      // Get LiveKit URL
      const livekitUrl =
        (import.meta as any).env.VITE_LIVEKIT_URL ||
        "wss://macrowave-qqgjtzze.livekit.cloud";

      // Connect to room
      await room.connect(livekitUrl, token, {
        autoSubscribe: true,
      });

      roomRef.current = room;

      // Check for existing participants
      room.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
          if (
            publication.kind === "audio" &&
            publication.track &&
            publication.isSubscribed
          ) {
            handleTrackSubscribed(
              publication.track as LiveKit.RemoteTrack,
              publication as LiveKit.RemoteTrackPublication,
              participant
            );
          }
        });
      });
    } catch (error) {
      setIsConnecting(false);
      setError(error as Error);

      if (error instanceof Error) {
        if (error.message.includes("Station is not active")) {
          toast.error("Station is offline", {
            description: "This station is not currently broadcasting",
          });
        } else {
          toast.error("Connection failed", {
            description: error.message,
          });
        }
      }
    }
  }, [station, isConnecting, joinStationAsViewer, handleTrackSubscribed, audioManager]);

  // Unmute audio (for when user clicks play after autoplay block)
  const unmute = useCallback(async () => {
    if (!isAutoplayBlocked) return;

    try {
      // Unmute and play all audio elements
      await audioManager.playAll();
      setIsAutoplayBlocked(false);
      setError(null);
    } catch (error) {
      console.error("[StationConnection] Failed to unmute:", error);
      setError(error as Error);
    }
  }, [isAutoplayBlocked, audioManager]);

  // Disconnect from station
  const disconnect = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.disconnect();
      roomRef.current.removeAllListeners();
      roomRef.current = null;
    }

    audioManager.cleanup();
    setIsConnected(false);
    setIsConnecting(false);
    setNowPlaying(null);
    setIsAutoplayBlocked(false);
    hasAttemptedAutoplay.current = false;
  }, [audioManager]);

  // Auto-connect on mount if requested and station is live
  useEffect(() => {
    if (autoConnect && station?.isActive && !isConnected && !isConnecting && !roomRef.current) {
      connect();
    }
  }, [autoConnect, station?.isActive, station?.id]); // Only depend on station ID and active status

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  return {
    isConnected,
    isConnecting,
    error,
    isAutoplayBlocked,
    connect,
    disconnect,
    unmute,
    nowPlaying,
  };
}